// On-chain stand-in for the Ref indexer, used when REACT_APP_INDEXER_URL is
// not set. It answers the indexer endpoints the core DEX screens need (pool
// lists, pool details, token lists, prices, a user's pools) straight from the
// exchange contract, in the indexer's response shapes. Swap volume and fees
// come from the exchange's cumulative pool volumes. TVL history and per-trade
// receipts have no on-chain source.
//
// Prices are derived from pool reserves, anchored at USD stablecoins
// (REACT_APP_USD_TOKEN_IDS, or the network's USDC/USDT) and propagated through
// simple pools, e.g. USDC -> wNEAR -> REF. When a token has no path to an
// anchor, its USD price is filled from Ref Finance. On testnet that is
// mainnet wNEAR's price, applied to wrap.testnet.

import getConfig, { univ4HookAllowed } from './config';
import db from '../store/RefDatabase';
import { refFiViewFunction, wallet } from './near';

const config = getConfig();

const POOL_PAGE = 100;
const SHARES_BATCH = 100;
const CACHE_MS = 15 * 1000;
const PRICE_HOPS = 4;
// A pool only prices a token when its priced side holds at least this much.
// Keeps dust pools (common on testnet) from producing absurd prices.
const MIN_PRICE_DEPTH_USD = Number(
  process.env.REACT_APP_MIN_PRICE_DEPTH_USD || 1
);
const LOW_TVL_USD = 1000;
// Parallel ft_metadata lookups; public RPCs rate-limit bursts.
const METADATA_CONCURRENCY = 8;

const USD_TOKEN_IDS: string[] = Array.from(
  new Set(
    (process.env.REACT_APP_USD_TOKEN_IDS || '')
      .split(',')
      .map((id) => id.trim())
      .concat([
        config.USDC_TOKEN_ID,
        config.USDT_TOKEN_ID,
        config.USDCe_TOKEN_ID,
        config.USDTe_TOKEN_ID,
      ])
      .filter(Boolean)
  )
);

interface SwapVolume {
  input: string;
  output: string;
}

interface ChainPool {
  id: number;
  pool_kind: string;
  token_account_ids: string[];
  amounts: string[];
  total_fee: number;
  shares_total_supply: string;
  amp: number;
  volumes?: SwapVolume[];
}

interface TokenMeta {
  symbol: string;
  decimals: number;
  name: string;
  icon: string;
}

type PriceMap = Record<
  string,
  { price: string; symbol: string; decimal: number }
>;

// Ref's public price list. CORS is open. Testnet has no equivalent, so
// testnet NEAR uses mainnet wNEAR's USD price.
const REF_PRICE_URL = 'https://indexer.ref.finance/list-token-price';
const REF_PRICE_CACHE_MS = 60 * 1000;

let refPriceCache: { at: number; prices: Promise<PriceMap> } | null = null;

const loadRefPrices = (): Promise<PriceMap> => {
  if (refPriceCache && Date.now() - refPriceCache.at < REF_PRICE_CACHE_MS) {
    return refPriceCache.prices;
  }
  const prices = fetch(REF_PRICE_URL)
    .then((response) => {
      if (!response.ok) throw new Error(String(response.status));
      return response.json();
    })
    .then((body): PriceMap => (body && typeof body === 'object' ? body : {}))
    .catch(() => {
      refPriceCache = null;
      return {};
    });
  refPriceCache = { at: Date.now(), prices };
  return prices;
};

// Local pool prices win. Ref only fills tokens this exchange cannot price.
const applyRefPrices = (prices: PriceMap, refPrices: PriceMap) => {
  const near = refPrices['wrap.near'];
  const nearPrice = Number(near?.price);
  if (Number.isFinite(nearPrice) && nearPrice > 0) {
    const entry = {
      price: String(near.price),
      symbol: near.symbol || 'wNEAR',
      decimal: near.decimal ?? 24,
    };
    if (!prices[config.WRAP_NEAR_CONTRACT_ID]) {
      prices[config.WRAP_NEAR_CONTRACT_ID] = entry;
    }
    if (!prices.NEAR) prices.NEAR = { ...entry, symbol: 'NEAR' };
  }
  if (config.networkId !== 'mainnet') return;
  Object.keys(refPrices).forEach((id) => {
    const entry = refPrices[id];
    const value = Number(entry?.price);
    if (!prices[id] && Number.isFinite(value) && value > 0) prices[id] = entry;
  });
};

// ---------- chain reads (cached) ----------

let poolsCache: { at: number; pools: Promise<ChainPool[]> } | null = null;

const loadPools = (): Promise<ChainPool[]> => {
  if (poolsCache && Date.now() - poolsCache.at < CACHE_MS) {
    return poolsCache.pools;
  }
  const pools = (async () => {
    const total: number = await refFiViewFunction({
      methodName: 'get_number_of_pools',
    });
    const pages = await Promise.all(
      Array.from({ length: Math.ceil(total / POOL_PAGE) }, (_, page) =>
        refFiViewFunction({
          methodName: 'get_pools',
          args: { from_index: page * POOL_PAGE, limit: POOL_PAGE },
        })
      )
    );
    return pages.flat().map((pool: any, id: number) => ({ ...pool, id }));
  })();
  poolsCache = { at: Date.now(), pools };
  pools.catch(() => (poolsCache = null));
  return pools;
};

const metadataCache: Record<string, Promise<TokenMeta | null>> = {};

// Tokens whose ft_metadata failed (deleted or non-NEP-141 contracts), so they
// are not re-queried on every page load.
const BAD_TOKENS_KEY = 'DEX_FALLBACK_BAD_TOKENS';
const BAD_TOKENS_TTL_MS = 24 * 60 * 60 * 1000;
const CONTRACT_ERROR =
  /does not exist|CodeDoesNotExist|MethodNotFound|MethodResolveError|wasm execution failed|GuestPanic/i;

const readBadTokens = (): Record<string, number> => {
  try {
    return JSON.parse(localStorage.getItem(BAD_TOKENS_KEY) || '{}');
  } catch (error) {
    return {};
  }
};

const markBadToken = (tokenId: string) => {
  try {
    const bad = readBadTokens();
    bad[tokenId] = Date.now();
    localStorage.setItem(BAD_TOKENS_KEY, JSON.stringify(bad));
  } catch (error) {}
};

// Reads the app's IndexedDB token cache first. Not ftGetTokenMetadata: on
// failure that returns a placeholder with made-up decimals, which would
// silently mis-price the token.
const fetchMetadata = async (tokenId: string): Promise<TokenMeta | null> => {
  const cached = await db.allTokens().where({ id: tokenId }).first();
  if (cached) return cached as TokenMeta;
  const meta: TokenMeta = await wallet
    .account()
    .viewFunction(tokenId, 'ft_metadata', {});
  if (!meta || typeof meta.decimals !== 'number') return null;
  await db.allTokens().put({
    id: tokenId,
    name: meta.name,
    symbol: meta.symbol,
    decimals: meta.decimals,
    icon: meta.icon,
  });
  return meta;
};

const loadMetadata = (tokenId: string): Promise<TokenMeta | null> => {
  if (!metadataCache[tokenId]) {
    const failedAt = readBadTokens()[tokenId];
    if (failedAt && Date.now() - failedAt < BAD_TOKENS_TTL_MS) {
      metadataCache[tokenId] = Promise.resolve(null);
    } else {
      metadataCache[tokenId] = fetchMetadata(tokenId)
        .then((meta) => {
          if (!meta) markBadToken(tokenId);
          return meta;
        })
        .catch((error): null => {
          // Only contract-side failures are permanent; network errors retry
          // on the next load.
          if (CONTRACT_ERROR.test(String(error?.message || error))) {
            markBadToken(tokenId);
          } else {
            delete metadataCache[tokenId];
          }
          return null;
        });
    }
  }
  return metadataCache[tokenId];
};

const loadMetadataMap = async (tokenIds: string[]) => {
  const ids = Array.from(new Set(tokenIds));
  const metas: (TokenMeta | null)[] = new Array(ids.length);
  let next = 0;
  const worker = async () => {
    while (next < ids.length) {
      const tokenIndex = next++;
      metas[tokenIndex] = await loadMetadata(ids[tokenIndex]);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(METADATA_CONCURRENCY, ids.length) }, worker)
  );
  return ids.reduce((metadataById, id, index) => {
    if (metas[index]) metadataById[id] = metas[index];
    return metadataById;
  }, {} as Record<string, TokenMeta>);
};

// ---------- derived data ----------

const readable = (amount: string, decimals: number) =>
  Number(amount) / Math.pow(10, decimals);

const computePrices = (
  pools: ChainPool[],
  meta: Record<string, TokenMeta>
): PriceMap => {
  const prices: Record<string, number> = {};
  USD_TOKEN_IDS.forEach((id) => (prices[id] = 1));

  const pairs = pools.filter(
    (pool) =>
      pool.pool_kind === 'SIMPLE_POOL' &&
      pool.token_account_ids.length === 2 &&
      pool.amounts.every((amount) => amount !== '0') &&
      pool.token_account_ids.every((id) => meta[id])
  );

  for (let hop = 0; hop < PRICE_HOPS; hop++) {
    // Best quote per token for this hop: the pool with the deepest priced side.
    const best: Record<string, { price: number; depth: number }> = {};
    pairs.forEach((pool) => {
      [0, 1].forEach((side) => {
        const token = pool.token_account_ids[side];
        const other = pool.token_account_ids[1 - side];
        if (prices[token] !== undefined || prices[other] === undefined) return;
        const depth =
          readable(pool.amounts[1 - side], meta[other].decimals) *
          prices[other];
        if (depth < MIN_PRICE_DEPTH_USD) return;
        const price =
          depth / readable(pool.amounts[side], meta[token].decimals);
        if (!best[token] || depth > best[token].depth) {
          best[token] = { price, depth };
        }
      });
    });
    const found = Object.keys(best);
    if (!found.length) break;
    found.forEach((token) => (prices[token] = best[token].price));
  }

  return Object.keys(prices).reduce((pricesById, id) => {
    if (meta[id]) {
      pricesById[id] = {
        price: prices[id].toString(),
        symbol: meta[id].symbol,
        decimal: meta[id].decimals,
      };
    }
    return pricesById;
  }, {} as PriceMap);
};

const snapshot = async () => {
  const pools = await loadPools();
  // Include empty pools. Their tokens are not priced, but the pool list links
  // by symbol, and the detail page has to resolve that same name.
  const meta = await loadMetadataMap(
    pools.flatMap((pool) => pool.token_account_ids).concat(USD_TOKEN_IDS)
  );
  const prices = computePrices(pools, meta);
  const refPricesPromise = loadRefPrices();
  try {
    const volumeRows: SwapVolume[][] = await refFiViewFunction({
      methodName: 'list_pool_volumes',
      args: { from_index: 0, limit: pools.length },
    });
    pools.forEach((pool, index) => {
      pool.volumes = volumeRows?.[index] || [];
    });
  } catch (error) {
    pools.forEach((pool) => {
      pool.volumes = pool.volumes || [];
    });
  }
  applyRefPrices(prices, await refPricesPromise);
  return { pools, meta, prices };
};

// A pool in the indexer's JSON shape.
const toIndexerPool = (
  pool: ChainPool,
  meta: Record<string, TokenMeta>,
  prices: PriceMap
) => {
  const sides = pool.token_account_ids.map((id, index) =>
    meta[id] && prices[id]
      ? readable(pool.amounts[index], meta[id].decimals) *
        Number(prices[id].price)
      : null
  );
  const priced = sides.filter((sideValue) => sideValue !== null);
  // A constant-product pool holds equal value on both sides, so 2x the smaller
  // priced side bounds TVL even when one token carries a bad price.
  const tvl =
    pool.pool_kind === 'SIMPLE_POOL' && priced.length
      ? 2 * Math.min(...priced)
      : priced.reduce((total, sideValue) => total + sideValue, 0);
  // Cumulative swap input is the only volume the exchange records. With no
  // indexer history, that total is what Volume(24h) can show.
  let volume = 0;
  (pool.volumes || []).forEach((entry, index) => {
    const tokenId = pool.token_account_ids[index];
    const token = meta[tokenId];
    const price = Number(prices[tokenId]?.price || 0);
    if (!token || !price || !entry?.input) return;
    volume += readable(entry.input, token.decimals) * price;
  });
  const fee = volume * (Number(pool.total_fee) / 10000);
  // 20% of the swap fee is the protocol share on this deployment (2000 bps).
  const lpFee = fee * 0.8;
  const apy = tvl > 0 ? ((lpFee * 365) / tvl) * 100 : 0;
  return {
    id: pool.id.toString(),
    pool_kind: pool.pool_kind,
    token_account_ids: pool.token_account_ids,
    token_symbols: pool.token_account_ids.map((id) => meta[id]?.symbol || id),
    amounts: pool.amounts,
    total_fee: pool.total_fee,
    shares_total_supply: pool.shares_total_supply,
    amp: pool.amp,
    tvl: tvl.toString(),
    token0_ref_price: prices[pool.token_account_ids[0]]?.price || '0',
    volume_24h: volume.toString(),
    fee_volume_24h: fee.toString(),
    apy,
    farm_apy: 0,
    is_farm: false,
    top: false,
  };
};

// ---------- endpoints ----------

const idList = (value: string | null, separator: RegExp = /[|,]/) =>
  (value || '')
    .split(separator)
    .map((entry) => entry.trim())
    .filter(Boolean);

const searchPools = async (params: URLSearchParams) => {
  const { pools, meta, prices } = await snapshot();
  let matchingPools = pools.map((pool) => toIndexerPool(pool, meta, prices));

  const ids = idList(params.get('pool_id_list'));
  if (ids.length)
    matchingPools = matchingPools.filter((pool) => ids.includes(pool.id));

  const type = params.get('type');
  if (type === 'classic')
    matchingPools = matchingPools.filter(
      (pool) => pool.pool_kind === 'SIMPLE_POOL'
    );
  if (type === 'stable')
    matchingPools = matchingPools.filter(
      (pool) => pool.pool_kind !== 'SIMPLE_POOL'
    );

  const query = (params.get('token_list') || '').toLowerCase().trim();
  if (query) {
    matchingPools = matchingPools.filter((pool) =>
      pool.token_account_ids.some(
        (id, index) =>
          id.toLowerCase().includes(query) ||
          pool.token_symbols[index].toLowerCase().includes(query)
      )
    );
  }

  if (params.get('hide_low_pool') === 'true') {
    matchingPools = matchingPools.filter(
      (pool) => Number(pool.tvl) >= LOW_TVL_USD
    );
  }

  const sortKey = params.get('sort') === 'fee' ? 'total_fee' : 'tvl';
  const direction = params.get('order_by') === 'asc' ? 1 : -1;
  matchingPools.sort(
    (poolA, poolB) =>
      direction * (Number(poolA[sortKey]) - Number(poolB[sortKey]))
  );

  const offset = Number(params.get('offset') || 0);
  const limit = Number(params.get('limit') || matchingPools.length);
  // The indexer's search endpoint reports fees as a fraction (0.003), not bps.
  const page = matchingPools
    .slice(offset, offset + limit)
    .map((pool) => ({ ...pool, total_fee: pool.total_fee / 10000 }));
  return { data: { list: page, total: matchingPools.length } };
};

const poolsByIds = async (ids: string[]) => {
  const { pools, meta, prices } = await snapshot();
  return ids
    .map((id) => pools[Number(id)])
    .filter(Boolean)
    .map((pool) => toIndexerPool(pool, meta, prices));
};

const accountPools = async (accountId: string) => {
  const { pools, meta, prices } = await snapshot();
  const batches = await Promise.all(
    Array.from(
      { length: Math.ceil(pools.length / SHARES_BATCH) },
      (_, batchIndex) =>
        refFiViewFunction({
          methodName: 'get_pool_shares_batch',
          args: {
            pool_ids: pools
              .slice(batchIndex * SHARES_BATCH, (batchIndex + 1) * SHARES_BATCH)
              .map((pool) => pool.id),
            account_id: accountId,
          },
        })
    )
  );
  const shares: string[] = batches.flat();
  return pools
    .filter((_, index) => shares[index] && shares[index] !== '0')
    .map((pool) => ({
      ...toIndexerPool(pool, meta, prices),
      share: shares[pool.id],
    }));
};

const whitelistedTokens = async () => {
  const ids: string[] = await refFiViewFunction({
    methodName: 'get_whitelisted_tokens',
  });
  const meta = await loadMetadataMap(ids);
  return Object.keys(meta).reduce((metadataById, id) => {
    metadataById[id] = meta[id];
    return metadataById;
  }, {} as Record<string, TokenMeta>);
};

// Two-token pools record each direction separately: token A input paired with
// token B output. That is the swap the pool has settled, with no block time.
const isNearId = (id: string) =>
  id === 'near' || id === 'NEAR' || id === config.WRAP_NEAR_CONTRACT_ID;

const sameToken = (left: string, right: string) =>
  left === right || (isNearId(left) && isNearId(right));

const token1PerToken0 = (
  sqrtPriceX96: string,
  decimals0: number,
  decimals1: number
) => {
  const sqrt = Number(sqrtPriceX96) / 2 ** 96;
  if (!Number.isFinite(sqrt) || sqrt <= 0) return 0;
  return sqrt * sqrt * 10 ** (decimals0 - decimals1);
};

// Price of `tokenId` in `baseId`: how much base one token buys.
const pairSpot = async (tokenId: string, baseId: string) => {
  const { pools, meta } = await snapshot();
  const classic = pools.find(
    (pool) =>
      pool.pool_kind === 'SIMPLE_POOL' &&
      pool.token_account_ids.length === 2 &&
      pool.token_account_ids.some((id) => sameToken(id, tokenId)) &&
      pool.token_account_ids.some((id) => sameToken(id, baseId))
  );
  if (classic) {
    const tokenSide = classic.token_account_ids.findIndex((id) =>
      sameToken(id, tokenId)
    );
    const baseSide = 1 - tokenSide;
    const tokenMeta = meta[classic.token_account_ids[tokenSide]];
    const baseMeta = meta[classic.token_account_ids[baseSide]];
    const tokenAmount = readable(
      classic.amounts[tokenSide],
      tokenMeta?.decimals ?? 0
    );
    const baseAmount = readable(
      classic.amounts[baseSide],
      baseMeta?.decimals ?? 0
    );
    if (tokenAmount > 0 && baseAmount > 0) return baseAmount / tokenAmount;
  }
  if (!config.UNIV4_CONTRACT_ID) return null;
  const clmmPools: {
    currency0: string;
    currency1: string;
    sqrt_price_x96: string;
    fee?: number;
    hooks?: string | null;
  }[] = await wallet
    .account()
    .viewFunction(config.UNIV4_CONTRACT_ID, 'get_pools', {
      from_index: 0,
      limit: 100,
    })
    .catch(() => []);
  const clmm = (clmmPools || []).find(
    (pool) =>
      (Number(pool.fee) & 0x800000) === 0 &&
      univ4HookAllowed(pool.hooks) &&
      [pool.currency0, pool.currency1].some((id) => sameToken(id, tokenId)) &&
      [pool.currency0, pool.currency1].some((id) => sameToken(id, baseId))
  );
  if (!clmm) return null;
  const [meta0, meta1] = await Promise.all([
    loadMetadata(clmm.currency0),
    loadMetadata(clmm.currency1),
  ]);
  if (!meta0 || !meta1) return null;
  const nearPerToken0 = token1PerToken0(
    clmm.sqrt_price_x96,
    meta0.decimals,
    meta1.decimals
  );
  if (!(nearPerToken0 > 0)) return null;
  const tokenIs0 = sameToken(clmm.currency0, tokenId);
  const baseIs0 = sameToken(clmm.currency0, baseId);
  if (tokenIs0 && !baseIs0) return nearPerToken0;
  if (!tokenIs0 && baseIs0) return 1 / nearPerToken0;
  return null;
};

const PAIR_HISTORY_KEY = 'zear_pair_price_history';
const DIMENSION_SECONDS: Record<string, number> = {
  D: 24 * 60 * 60,
  W: 7 * 24 * 60 * 60,
  M: 30 * 24 * 60 * 60,
  Y: 365 * 24 * 60 * 60,
};

const pairPriceReport = async (
  tokenId: string,
  baseId: string,
  dimension: string
) => {
  const price = await pairSpot(tokenId, baseId);
  if (!(price > 0) || !Number.isFinite(price)) {
    return { symbol: tokenId, contract_address: tokenId, price_list: [] };
  }
  const now = Math.floor(Date.now() / 1000);
  const key = `${tokenId}|${baseId}`;
  let history: { price: number; date_time: number }[] = [];
  try {
    const stored = JSON.parse(localStorage.getItem(PAIR_HISTORY_KEY) || '{}');
    history = Array.isArray(stored[key]) ? stored[key] : [];
    const last = history[history.length - 1];
    if (
      !last ||
      Math.abs(last.price - price) / price > 1e-6 ||
      now - last.date_time > 60
    ) {
      history.push({ price, date_time: now });
    }
    history = history.slice(-400);
    stored[key] = history;
    localStorage.setItem(PAIR_HISTORY_KEY, JSON.stringify(stored));
  } catch (error) {
    history = [{ price, date_time: now }];
  }
  const span = DIMENSION_SECONDS[dimension];
  const start =
    dimension === 'All' && history.length
      ? history[0].date_time
      : now - (span || DIMENSION_SECONDS.D);
  const inWindow = history.filter((point) => point.date_time >= start);
  const points = inWindow.length ? inWindow : history.slice(-1);
  if (points.length < 2) {
    points.unshift({ price: points[0].price, date_time: start });
  }
  return {
    symbol: tokenId,
    contract_address: tokenId,
    price_list: points,
  };
};

const recordedSwaps = (pool?: ChainPool) => {
  if (!pool || pool.token_account_ids.length !== 2) return [];
  const volumes = pool.volumes || [];
  return [0, 1]
    .map((inputSide) => {
      const outputSide = 1 - inputSide;
      // The input token's volume stores amount in, and the amount out of the
      // other token.
      const swapIn = volumes[inputSide]?.input || '0';
      const swapOut = volumes[inputSide]?.output || '0';
      if (swapIn === '0' || swapOut === '0') return null;
      return {
        token_in: pool.token_account_ids[inputSide],
        token_out: pool.token_account_ids[outputSide],
        swap_in: swapIn,
        swap_out: swapOut,
        timestamp: 'On-chain',
        tx_id: '',
        receipt_id: `onchain-${pool.id}-${inputSide}`,
      };
    })
    .filter(Boolean);
};

// The exchange does not store each deposit. The reserves are the liquidity
// still in the pool, which is what an add leaves behind before any swap.
const recordedLiquidity = (pool?: ChainPool) => {
  if (!pool || !pool.amounts?.some((amount) => amount && amount !== '0')) {
    return [];
  }
  return [
    {
      method_name: 'add_liquidity',
      timestamp: 'On-chain',
      amounts: JSON.stringify(pool.amounts),
      tx_id: '',
      receipt_id: `onchain-liquidity-${pool.id}`,
      pool_id: String(pool.id),
    },
  ];
};

const fallbackIndexerResponse = async (path: string): Promise<any> => {
  const url = new URL(path, 'http://indexer.local');
  const params = url.searchParams;
  const route = url.pathname;

  if (route === '/list-top-pools') {
    // Routing input: only pools that hold liquidity.
    const { pools, meta, prices } = await snapshot();
    return pools
      .filter((pool) => pool.shares_total_supply !== '0')
      .map((pool) => toIndexerPool(pool, meta, prices));
  }
  if (route === '/pool/search') return searchPools(params);
  if (route === '/list-pools') {
    // Paged form when a type is given, a plain array (fees in bps) otherwise.
    if (params.get('type')) return searchPools(params);
    const { pools, meta, prices } = await snapshot();
    return pools.map((pool) => toIndexerPool(pool, meta, prices));
  }
  if (route === '/get-pool') {
    return (await poolsByIds([params.get('pool_id')]))[0] || null;
  }
  if (route === '/list-pools-by-ids')
    return poolsByIds(idList(params.get('ids')));
  if (route === '/pool/detail') {
    const [pool] = await poolsByIds([params.get('pool_id')]);
    return { data: pool || null };
  }
  if (route === '/list-pools-by-tokens') {
    const [firstTokenId, secondTokenId] = [
      params.get('token0'),
      params.get('token1'),
    ];
    const { pools, meta, prices } = await snapshot();
    return pools
      .filter(
        (pool) =>
          pool.token_account_ids.includes(firstTokenId) &&
          pool.token_account_ids.includes(secondTokenId)
      )
      .map((pool) => toIndexerPool(pool, meta, prices));
  }
  if (route.startsWith('/liquidity-pools/')) {
    return accountPools(
      decodeURIComponent(route.slice('/liquidity-pools/'.length))
    );
  }
  if (route === '/list-token-price') return (await snapshot()).prices;
  if (route === '/list-token-price-by-ids') {
    const { prices } = await snapshot();
    return idList(params.get('ids')).map((id) => prices[id]?.price || '0');
  }
  if (route === '/get-token-price') {
    const { prices } = await snapshot();
    return { price: prices[params.get('token_id')]?.price || '-' };
  }
  if (route === '/all-pool-data') {
    // Header stats from the same cumulative swap volumes as each pool.
    const { pools, meta, prices } = await snapshot();
    const indexed = pools.map((pool) => toIndexerPool(pool, meta, prices));
    const tvl = indexed.reduce((total, pool) => total + Number(pool.tvl), 0);
    const volume = indexed.reduce(
      (total, pool) => total + Number(pool.volume_24h),
      0
    );
    const fee = indexed.reduce(
      (total, pool) => total + Number(pool.fee_volume_24h),
      0
    );
    return {
      data: {
        tvl: tvl.toString(),
        volume_24h: volume.toString(),
        fee: fee.toString(),
      },
    };
  }
  if (route === '/get-recent-transaction-swap') {
    const pools = (await snapshot()).pools;
    const pool = pools[Number(params.get('pool_id'))];
    return recordedSwaps(pool);
  }
  if (route === '/get-recent-transaction-liquidity') {
    const pools = (await snapshot()).pools;
    const pool = pools[Number(params.get('pool_id'))];
    return recordedLiquidity(pool);
  }
  if (route === '/list-token') return whitelistedTokens();
  if (route === '/timestamp') return { ts: Math.floor(Date.now() / 1000) };
  if (route === '/get-service-version') return { version: 'on-chain' };
  if (route === '/get-token-flow') return { data: null };
  if (route === '/token-price-report') {
    return pairPriceReport(
      params.get('token') || '',
      params.get('base_token') || '',
      params.get('dimension') || 'D'
    );
  }

  // TVL history, per-trade receipts, limit orders: nothing further on-chain.
  return [];
};

const jsonResponse = (body: any) =>
  new Response(JSON.stringify(body), {
    status: 200,
    headers: { 'Content-Type': 'application/json' },
  });

// Drop-in for `fetch(indexerUrl + path, init)`. A stopped indexer falls back
// to the on-chain reads so the screens keep their current pools and prices.
export const indexerFetch = (path: string, init?: RequestInit) => {
  const onChain = () => {
    if (init?.method && init.method !== 'GET') {
      return Promise.resolve(new Response(null, { status: 204 }));
    }
    return fallbackIndexerResponse(path).then(jsonResponse);
  };
  // The indexer 500s on this route (it was reading zear-dex.near, which has
  // no code). The on-chain series is the chart until that deploy is live.
  if (config.indexerUrl && !path.startsWith('/token-price-report')) {
    return fetch(config.indexerUrl + path, init)
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response;
      })
      .catch(() => onChain());
  }
  return onChain();
};

// Drop-in for `fetch(dataServiceApiUrl + path, init)`. The data service only
// serves historical analytics, so without one every series is empty.
export const dataServiceFetch = (path: string, init?: RequestInit) => {
  if (config.dataServiceApiUrl) {
    return fetch(config.dataServiceApiUrl + path, init)
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response;
      })
      .catch(() => jsonResponse([]));
  }
  return Promise.resolve(jsonResponse([]));
};
